import React from 'react'
import { compose, withProps } from 'recompose'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import PropTypes from 'prop-types'
import features from '../../components/features'
import settings from '../../components/settings'
import withHelmet from '../../components/with-helmet/withHelmet'
import Loader from '../../components/loader/Loader'
import { getService } from '../bada/content'
import { SIMPLY_BOOK_ME, CUSTOMER_FORM } from '../bada/reducer/Wizard'
import AdditionalInfo from './AdditionalInfo'
import CustomerInfo from './CustomerInfo'
import Images from './Images'
import MeetingEditor from './meeting-editor/MeetingEditor'
import MeetingViewer from './meeting-viewer/MeetingViewer'
import Questions from './Questions'
import { Section, SectionHeader, SectionSpacer } from './sharedUI'
import {
  checkEmpty,
  checkMeetingAllowUpdate,
  checkMeetingAllowCancel,
  checkMeetingHasPassed,
} from './utils'
import useTranslation from '../../../utils/translation'
import { ensureSubsystem } from '../../../utils/meetingType'
import { useCms } from '../../components/cms/CmsContext'
import { OnlineMeeting } from './OnlineMeeting'
import { useHookAsHoc } from '../../hooks/useHookAsHoc'
import {
  useGetAppointment,
  useGetStores,
} from '../../../utils/badaAppointmentApi'

const MainContainer = styled.div`
  padding: 20px;
  ${(props) => props.theme.media.max.xs`
    padding: 5px
  `};
`

const StyledHeadline = styled.h1`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: 32px;
  font-weight: 300;
  margin: 30px auto 40px auto;
  text-align: center;
  max-width: 500px;
  ${(props) => props.theme.media.min.sm`
    font-size: 32px;
    margin: 0px auto 20px auto;
  `}
`

const SectionsContainer = styled.div`
  padding: 0px;
  border: ${theme('appointmentDetails.sectionsContainer.border')};
  border-radius: ${theme('appointmentDetails.sectionsContainer.borderRadius')};
  max-width: 840px;
  margin: 0px auto;
  margin-bottom: 40px;
  background-color: #fff;
`

const PrepComment = styled.div`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-weight: 300;
`

const NotFound = styled.div`
  font-family: ${theme('typography.paragraph.fontFamily')};
  text-align: center;
  margin: 40px auto;
`

const AlertMessage = styled.div`
  margin-bottom: 20px;
  width: 100%;
  padding: 20px;
  background-color: ${theme('colors.default')};
  color: #fff;
  border-radius: 8px;
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: 24px;
`

const AppointmentDetailsPage = ({
  appointment,
  refetchAppointment,
  store,
  storesData,
}) => {
  const { services } = useCms()
  const t = useTranslation('appointment-details')

  if (!appointment) return <Loader duration={3} />
  if (appointment && !appointment.id) {
    return <NotFound>The appointment was not found.</NotFound>
  }
  const {
    id,
    updated,
    selectedDate,
    selectedTime,
    selectedEndTime,
    simplyBookMeId,
    externalAppointmentId,
    simplyBookMeStatus,
    appointmentPreparations,
    storeId,
    serviceProviderId,
    serviceProviderName,
    simplyBookMeSubsystem,
    externalBookingSystem,
    externalMeetingProvider,
    externalStoreId,
    designerSelectedByCustomer,
    cancelledBy,
    cancellationReason,
  } = appointment
  let storeName = null
  if (store) {
    storeName = store.storeName
  }
  const { questions } = appointmentPreparations || {}
  const meetingAllowUpdate = checkMeetingAllowUpdate(selectedDate, selectedTime)
  const meetingAllowCancel = checkMeetingAllowCancel(selectedDate, selectedTime)
  const meetingHasPassed = checkMeetingHasPassed(selectedDate, selectedTime)
  const meetingEndHasPassed = checkMeetingHasPassed(
    selectedDate,
    selectedEndTime
  )
  const defaultBookingSystem = selectedTime ? SIMPLY_BOOK_ME : CUSTOMER_FORM

  const service = getService({
    appointment,
    services,
    bookingSystem: externalBookingSystem || defaultBookingSystem,
    storeSubSystem: simplyBookMeSubsystem,
  })

  const filteredMeetingTypesBySubsystem = services?.filter(
    (m) =>
      ensureSubsystem(m, simplyBookMeSubsystem, settings.serviceMappings) &&
      m.type === 'simplybookme'
  )

  const orgCmsServiceQuestions = (service && service.questions) || []
  const isContactAppointment = () => {
    if (
      externalMeetingProvider === 'contactemail-appointment' ||
      externalMeetingProvider === 'contactphone-appointment'
    ) {
      return true
    }
    return false
  }

  const getServiceName = () => {
    if (features.kratos && isContactAppointment()) {
      return t(externalMeetingProvider)
    }
    return service && service.heading
  }

  const isSlotBooking =
    externalBookingSystem === 'simplybookme' && selectedDate && selectedTime
  const isVirtual =
    !!service?.code?.includes('video') || !!service?.code?.includes('discover')
  const isSubsystem3 =
    simplyBookMeSubsystem === 'magnet3' ||
    simplyBookMeSubsystem === 'magnettest3'
  const isPassedOrCanceled =
    simplyBookMeStatus === 'canceled' || meetingEndHasPassed

  const showOnlineMeeting =
    !isSubsystem3 &&
    isVirtual &&
    features.useTeamsOnlineMeeting &&
    (!features.teamsOnlineMeetingStores ||
      features.teamsOnlineMeetingStores.includes(parseInt(storeId, 10)))

  return (
    <MainContainer>
      <StyledHeadline>{t('heading')}</StyledHeadline>
      <SectionsContainer>
        <SectionSpacer noBottomPadding>
          <Section>
            {meetingHasPassed && (
              <AlertMessage>{t('appointment-passed')}</AlertMessage>
            )}
            {(isSlotBooking && features.editableAppointmentDetails && (
              <MeetingEditor
                externalBookingSystem={externalBookingSystem || SIMPLY_BOOK_ME}
                appointmentId={id}
                selectedDate={checkEmpty(selectedDate)}
                selectedTime={checkEmpty(selectedTime)}
                selectedEndTime={checkEmpty(selectedEndTime)}
                serviceId={service && service.id}
                services={filteredMeetingTypesBySubsystem}
                externalAppointmentId={externalAppointmentId || simplyBookMeId}
                locationId={externalStoreId}
                simplyBookMeStatus={simplyBookMeStatus}
                simplyBookMeSubsystem={simplyBookMeSubsystem}
                serviceProviderId={serviceProviderId}
                serviceProviderName={serviceProviderName}
                storeId={storeId && parseInt(storeId, 10)}
                storesData={storesData}
                onCancel={refetchAppointment}
                onUpdate={refetchAppointment}
                canceled={simplyBookMeStatus === 'canceled'}
                meetingAllowUpdate={meetingAllowUpdate}
                meetingAllowCancel={meetingAllowCancel}
                designerSelectedByCustomer={designerSelectedByCustomer}
                cancelledBy={cancelledBy}
                cancellationReason={cancellationReason}
              />
            )) || (
              <MeetingViewer
                meetingHasPassed={meetingHasPassed}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                storeName={storeName}
                serviceName={getServiceName(appointment, service)}
              />
            )}
          </Section>
          {showOnlineMeeting && (
            <Section>
              <OnlineMeeting
                appointmentId={id}
                updated={updated}
                readOnly={isPassedOrCanceled}
                appointment={appointment}
              />
            </Section>
          )}
          <CustomerInfo {...appointment} />
          <AdditionalInfo {...appointment} />
        </SectionSpacer>
      </SectionsContainer>
      <SectionsContainer>
        <Questions
          questions={questions}
          orgQuestions={orgCmsServiceQuestions}
          {...appointment}
        />
        {appointmentPreparations &&
          (appointmentPreparations.kitchenScetchImagesUrls.length > 0 ||
            appointmentPreparations.currentKitchenImagesUrls.length > 0 ||
            appointmentPreparations.comment) && (
            <>
              <SectionSpacer>
                <Images
                  appointmentPreparations={appointmentPreparations}
                  features={features}
                />
              </SectionSpacer>
              {appointmentPreparations.comment && (
                <SectionSpacer>
                  <SectionHeader>{t('prep-form.comments')}</SectionHeader>
                  <PrepComment>
                    {appointmentPreparations.comment || '-'}
                  </PrepComment>
                </SectionSpacer>
              )}
            </>
          )}
      </SectionsContainer>
    </MainContainer>
  )
}

AppointmentDetailsPage.propTypes = {
  appointment: PropTypes.shape(),
  store: PropTypes.shape(),
  refetchAppointment: PropTypes.func,
}

AppointmentDetailsPage.defaultProps = {
  appointment: null,
  store: null,
  refetchAppointment: null,
}

const enhance = compose(
  useHookAsHoc('data', ({ match }) =>
    useGetAppointment(match.params.appointmentId)
  ),
  withProps(({ data }) => ({
    appointment: data && data.data,
    refetchAppointment: data && data.refetch,
  })),
  withHelmet(() => ({
    robots: 'noindex,nofollow',
  })),
  useHookAsHoc(
    'storesData',
    () => useGetStores(true),
    ({ isLoading, ...rest }) => ({ ...rest, loading: isLoading })
  ),
  withProps(({ storesData, appointment }) => ({
    store: storesData?.data?.find(
      (item) =>
        item.id === appointment?.storeId &&
        !settings.excludedStudioTypes.includes(item.storeType)
    ),
  }))
)

export default enhance(AppointmentDetailsPage)
